import { useEffect, useState } from 'react';
import { formatDate } from '../../utils/formatters';
import { Button, Typography, Link, MenuItem, Stack, TextField } from '@mui/material';
const STATUS_OPTIONS = [
  {
    value: 'NEW',
    label: 'חדש',
  },
  {
    value: 'IN_PROGRESS',
    label: 'בטיפול',
  },
  {
    value: 'RESOLVED',
    label: 'טופל',
  },
];
const SUBJECT_LABELS = {
  PERMANENTLY_CLOSED: 'המקום סגור לצמיתות',
  OPENING_HOURS_INCORRECT: 'שעות הפתיחה לא נכונות',
  NAME_INCORRECT: 'שם המקום לא נכון',
  ADDRESS_INCORRECT: 'הכתובת לא נכונה',
  LOCATION_INCORRECT: 'המיקום במפה לא נכון',
  DUPLICATE_PLACE: 'המקום מופיע יותר מפעם אחת',
  OTHER: 'נושא אחר',
};
const ReportDialog = ({ report, open, onClose, onSave, loading }) => {
  const [form, setForm] = useState({
    status: 'NEW',
    resolution: '',
  });
  const [error, setError] = useState('');
  useEffect(() => {
    if (!report) return;
    setForm({
      status: report.status || 'NEW',
      resolution: report.resolution || '',
    });
    setError('');
  }, [report]);
  if (!open || !report) return null;
  const submit = async (event) => {
    event.preventDefault();
    if (form.status === 'RESOLVED' && !form.resolution.trim()) {
      setError('כדי לסמן את הדיווח כטופל צריך לפרט מה נעשה');
      return;
    }
    await onSave(report.reportId, {
      status: form.status,
      resolution: form.resolution.trim(),
    });
    onClose();
  };
  return (
    <Stack
      onMouseDown={onClose}
      sx={(theme) => ({
        position: 'fixed',
        inset: '0',
        zIndex: '30',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'rgba(21, 33, 31, .56)',
        [theme.breakpoints.down('sm')]: {
          padding: '0',
        },
        backdropFilter: 'blur(6px)',
      })}
    >
      <Stack
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
        dir="rtl"
        sx={(theme) => ({
          width: 'min(900px, 100%)',
          height: 'calc(100vh - 48px)',
          maxHeight: 'calc(100vh - 48px)',
          minHeight: '0',
          display: 'grid',
          gridTemplateRows: 'auto auto minmax(0, 1fr) auto',
          contain: 'layout',
          background: 'var(--panel)',
          borderRadius: '20px',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
          textAlign: 'right',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100vh',
            maxHeight: '100vh',
            minHeight: '100vh',
            borderRadius: '0',
          },
          border: '1px solid rgba(255,255,255,.45)',
        })}
      >
        <Stack
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            padding: '20px 24px',
            borderBottom: '1px solid var(--line)',
            [theme.breakpoints.down('sm')]: {
              padding: '16px',
            },
          })}
        >
          <Stack>
            <Typography
              sx={{
                margin: '0 0 8px',
                color: 'var(--primary)',
                fontSize: '12px',
                fontWeight: '800',
                letterSpacing: '0',
                textTransform: 'uppercase',
                marginBottom: '9px',
              }}
            >
              דיווח על מקום
            </Typography>
            <Typography>{report.placeName}</Typography>
            <Typography>{report.reportId}</Typography>
          </Stack>
          <Button
            type="button"
            onClick={onClose}
            aria-label="סגירה"
            sx={{
              width: '42px',
              height: '42px',
              minHeight: '42px',
              borderRadius: '50%',
              padding: '0',
              fontSize: '28px',
              lineHeight: '1',
              background: '#eef2f6',
              color: 'var(--text)',
            }}
          >
            ×
          </Button>
        </Stack>

        <Stack
          sx={(theme) => ({
            minWidth: '0',
            minHeight: '0',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarGutter: 'stable',
            padding: '15px',
            display: 'grid',
            alignContent: 'start',
            gap: '24px',
            [theme.breakpoints.down('sm')]: {
              padding: '14px',
            },
          })}
        >
          <Stack
            sx={{
              border: '1px solid var(--line)',
              borderRadius: '14px',
              padding: '16px',
              background: '#fff',
            }}
          >
            <Stack
              sx={{
                display: 'none',
                justifyContent: 'space-between',
                alignItems: 'end',
                gap: '12px',
                marginBottom: '14px',
              }}
            >
              <Typography>פרטי הדיווח</Typography>
            </Stack>
            <Stack
              sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '14px',
                margin: '0',
                '& div': {
                  minWidth: '0',
                },
                '& dt': {
                  color: 'var(--muted)',
                  fontSize: '12px',
                  fontWeight: '700',
                },
                '& dd': {
                  margin: '5px 0 0',
                  overflowWrap: 'anywhere',
                },
                '& a': {
                  color: 'var(--primary)',
                },
                [theme.breakpoints.down('sm')]: {
                  gridTemplateColumns: '1fr',
                },
              })}
            >
              <Stack>
                <Typography>מזהה דיווח</Typography>
                <Typography>{report.reportId}</Typography>
              </Stack>
              <Stack>
                <Typography>Place ID</Typography>
                <Typography>{report.placeId}</Typography>
              </Stack>
              <Stack>
                <Typography>נושא</Typography>
                <Typography>{SUBJECT_LABELS[report.subject] || report.subject}</Typography>
              </Stack>
              <Stack>
                <Typography>שם המדווח</Typography>
                <Typography>{report.reporterName}</Typography>
              </Stack>
              <Stack>
                <Typography>טלפון</Typography>
                <Typography>
                  <Link href={`tel:${report.phone}`}>{report.phone}</Link>
                </Typography>
              </Stack>
              <Stack>
                <Typography>נוצר</Typography>
                <Typography>{formatDate(report.createdAt)}</Typography>
              </Stack>
              <Stack>
                <Typography>עודכן</Typography>
                <Typography>{formatDate(report.updatedAt)}</Typography>
              </Stack>
            </Stack>
            <Stack
              sx={{
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px solid var(--line)',
                '& p': {
                  margin: '8px 0 0',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6',
                },
              }}
            >
              <Typography>תוכן הדיווח</Typography>
              <Typography>{report.details}</Typography>
            </Stack>
          </Stack>

          <Stack
            sx={{
              border: '1px solid var(--line)',
              borderRadius: '14px',
              padding: '16px',
              background: '#fff',
            }}
          >
            <Stack
              sx={{
                display: 'none',
                justifyContent: 'space-between',
                alignItems: 'end',
                gap: '12px',
                marginBottom: '14px',
              }}
            >
              <Typography>טיפול בדיווח</Typography>
            </Stack>
            <Stack
              sx={{
                display: 'grid',
                gap: '14px',
                '& textarea': {
                  direction: 'rtl',
                  textAlign: 'right',
                  fontFamily: 'inherit',
                },
              }}
            >
              <Stack>
                סטטוס
                <TextField
                  value={form.status}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                  select
                  fullWidth
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack>
                מה נעשה
                <TextField
                  rows="6"
                  value={form.resolution}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      resolution: event.target.value,
                    }));
                    setError('');
                  }}
                  placeholder="תיאור הפעולה שבוצעה או הערות לטיפול"
                  multiline
                  fullWidth
                />
              </Stack>
              {!!report.handledBy?.name && (
                <Typography
                  sx={(theme) => ({
                    color: 'var(--muted)',
                    [theme.breakpoints.down('lg')]: {
                      margin: '4px 0 0',
                      fontSize: '12px',
                      display: 'none',
                    },
                    margin: '2px 0 0',
                    fontSize: '11px',
                  })}
                >
                  טופל לאחרונה על ידי {report.handledBy.name}
                </Typography>
              )}
              {!!error && (
                <Typography
                  sx={{
                    borderColor: '#fecdca',
                    background: '#fef3f2',
                    color: 'var(--danger)',
                    fontWeight: '700',
                  }}
                >
                  {error}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Stack
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '16px',
            padding: '20px 24px',
            borderBottom: '0',
            borderTop: '1px solid var(--line)',
            [theme.breakpoints.down('sm')]: {
              padding: '16px',
              position: 'sticky',
              bottom: '0',
              flexWrap: 'wrap',
              background: '#fff',
              '& button': {
                flex: '1 1 120px',
              },
            },
          })}
        >
          <Button
            type="button"
            onClick={onClose}
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            ביטול
          </Button>
          <Button disabled={loading}>שמירת טיפול</Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
export { SUBJECT_LABELS, STATUS_OPTIONS };
export default ReportDialog;
